<template>
  <div
    id="wysiwyg-ck-editor"
    :style="`${validate ? 'border: 1px solid #dc3545;' : ''}`"
    :class="`${customClass}`"
  >
    <ckeditor
      :id="id"
      :model-value="content"
      :editor="editor"
      :config="editorConfig"
      :data-cy="dataCyId"
      :disabled="isDisabled"
      @input="changeData"
      @blur="handleFocusOut"
      @ready="onEditorReady"
      @focus="handleFocus"
    ></ckeditor>
  </div>
</template>

<script lang="ts" setup>
import {
  Bold,
  Code,
  Italic,
  Strikethrough,
  Subscript,
  Underline,
} from "@ckeditor/ckeditor5-basic-styles";
import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";
import { Essentials } from "@ckeditor/ckeditor5-essentials";
import { Image, ImageInsert, ImageUpload } from "@ckeditor/ckeditor5-image";
import { List } from "@ckeditor/ckeditor5-list";
import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
import { ref, watch, defineExpose, Ref } from "vue";
import { CustomBold } from "./customEditor/customBold";
import { CustomItalic } from "./customEditor/customItalic";
import { CustomUnderLine } from "./customEditor/customUnderline";
import { Base64UploadAdapter } from "@ckeditor/ckeditor5-upload";
import CKEditor from "@ckeditor/ckeditor5-vue";
import Link from "@ckeditor/ckeditor5-link/src/link";

const ICON_TEXT_MOTE = `
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M13.55 19H15.55L10.55 5H8.54999L3.54999 19H5.54999L6.94999 15H12.05L13.55 19ZM7.64999 13L9.54999 7.8L11.45 13H7.64999ZM20.45 17.5C20.45 17.7967 20.362 18.0867 20.1972 18.3334C20.0324 18.58 19.7981 18.7723 19.524 18.8858C19.2499 18.9993 18.9483 19.0291 18.6574 18.9712C18.3664 18.9133 18.0991 18.7704 17.8893 18.5607C17.6795 18.3509 17.5367 18.0836 17.4788 17.7926C17.4209 17.5017 17.4506 17.2001 17.5642 16.926C17.6777 16.6519 17.87 16.4176 18.1166 16.2528C18.3633 16.088 18.6533 16 18.95 16C19.3478 16 19.7293 16.158 20.0106 16.4393C20.292 16.7206 20.45 17.1022 20.45 17.5ZM20.45 13.5C20.45 13.7967 20.362 14.0867 20.1972 14.3334C20.0324 14.58 19.7981 14.7723 19.524 14.8858C19.2499 14.9994 18.9483 15.0291 18.6574 14.9712C18.3664 14.9133 18.0991 14.7704 17.8893 14.5607C17.6795 14.3509 17.5367 14.0836 17.4788 13.7926C17.4209 13.5017 17.4506 13.2001 17.5642 12.926C17.6777 12.6519 17.87 12.4176 18.1166 12.2528C18.3633 12.088 18.6533 12 18.95 12C19.3478 12 19.7293 12.158 20.0106 12.4393C20.292 12.7206 20.45 13.1022 20.45 13.5ZM20.45 9.5C20.45 9.79667 20.362 10.0867 20.1972 10.3334C20.0324 10.58 19.7981 10.7723 19.524 10.8858C19.2499 10.9994 18.9483 11.0291 18.6574 10.9712C18.3664 10.9133 18.0991 10.7704 17.8893 10.5607C17.6795 10.3509 17.5367 10.0836 17.4788 9.79264C17.4209 9.50166 17.4506 9.20006 17.5642 8.92597C17.6777 8.65189 17.87 8.41762 18.1166 8.2528C18.3633 8.08797 18.6533 8 18.95 8C19.3478 8 19.7293 8.15804 20.0106 8.43934C20.292 8.72064 20.45 9.10218 20.45 9.5Z"/>
  </svg>
`;
const ckeditor = CKEditor.component;
const $emit = defineEmits([
  "on:ready",
  "update:value",
  "update:modelValue",
  "update:blur",
  "update:focus",
  "update:keydown",
]);
const editableRef: Ref<any> = ref(null);
defineExpose({ editableRef });
const editor = ClassicEditor;

const props = defineProps({
  /** The model and value of the input */
  modelValue: { type: null, required: true },
  includeImage: { type: Boolean, default: true },
  validate: { type: Boolean, default: false },
  dataCyId: { type: String, default: "" },
  isDisabled: { type: Boolean, default: false },
  customClass: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  height: { type: String, default: "70px" },
  maxWidth: { type: String, default: "100%" },
  convertHtml: { type: Boolean, default: false },
  id: { type: String, default: "wysiwyg-ck-editor-input" },
});
const editorConfig = {
  plugins: [
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Code,
    Subscript,
    List,
    Essentials,
    Image,
    // ImageInsert,
    // ImageUpload,
    Paragraph,
    Link,
    CustomBold,
    CustomItalic,
    CustomUnderLine,
    Base64UploadAdapter,
    ...(props.includeImage ? [Image, ImageInsert, ImageUpload] : []),
  ],
  toolbar: [
    "Bold",
    "Italic",
    "CustomUnderline",
    "bulletedList",
    "numberedList",
    {
      label: "Basic styles",
      icon: ICON_TEXT_MOTE,
      items: ["strikethrough", "code", "subscript"],
    },
    "|",
    "link",
    "imageUpload",
  ].filter((item) => !(item === "imageUpload" && !props.includeImage)),
  placeholder: props.placeholder,
};

const content = ref(props.modelValue ?? "");

watch(
  () => props.modelValue,
  (newValue) => {
    content.value = newValue;
  },
);

watch(
  () => content.value,
  (newValue) => {
    $emit("update:value", newValue);
  },
);

const changeData = (value: string) => {
  $emit("update:value", value);
};

const onEditorReady = (editor) => {
  const editable = editor.ui.view.editable?.element;

  if (!editable) return;

  editableRef.value = editor.ui.view.editable.element;
  $emit("on:ready", editor);

  editable.addEventListener("keydown", (evt) => {
    $emit("update:keydown", evt);
  });

  if (!props.convertHtml) return;

  editor.plugins
    .get("ClipboardPipeline")
    .on("inputTransformation", (evt, data) => {
      const plainText = data.dataTransfer.getData("text/plain");

      if (plainText) {
        data.content = editor.data.htmlProcessor.toView(plainText);
      }
    });
};

const handleFocusOut = () => {
  $emit("update:blur");
};

const handleFocus = () => {
  $emit("update:focus");
};
</script>

<style lang="scss">
:root {
  --ck-border-radius: 6px;
  --ck-color-base-border: #eff0f1;
  --ck-color-focus-border: #eff0f1;
  --ck-color-button-on-color: #387dff;
}

.ck-powered-by-balloon {
  display: none !important;
}

#wysiwyg-ck-editor {
  font-family: Poppins;

  p,
  ul,
  ol {
    font-size: 13px;
    color: #231f20;
  }

  ul {
    margin-left: 18px;
  }

  ol {
    margin-left: 13px;
  }

  strong {
    font-weight: 500;
  }

  .ck-toolbar {
    height: 42px;
    background: var(--UI-rule-50, rgba(239, 240, 241, 0.5));
    padding: 10px 15px;
  }

  .ck-button {
    margin-top: 0px;
    margin-bottom: 0px;
    margin-right: 16px;
    padding: 0;
  }

  .ck-content {
    padding: 10px 25px;
    box-shadow: none !important;
  }

  .ck-toolbar__separator {
    margin: 0;
    background-color: transparent;
    width: 24px;
  }

  .ck-editor__editable {
    min-height: v-bind(height);
    max-height: v-bind(height);
    max-width: v-bind(maxWidth);
    overflow-y: auto;
  }
}
</style>
